import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  History,
  Search,
  RefreshCw,
  User,
  Clock,
  FileText,
  Briefcase,
  AlertTriangle,
  Building2,
  CheckCircle,
  XCircle,
  Edit,
  Plus,
  Trash2,
  Eye,
  ArrowRight,
  Filter,
} from 'lucide-react';

const ENTIDAD_TIPOS = [
  { value: 'all', label: 'Todas las entidades' },
  { value: 'CASO', label: 'Casos' },
  { value: 'ALERTA', label: 'Alertas' },
  { value: 'EMPRESA', label: 'Empresas' },
  { value: 'APROBACION', label: 'Aprobaciones' },
  { value: 'USUARIO', label: 'Usuarios' },
];

const ACCION_ICONS = {
  CREADO: Plus,
  ACTUALIZADO: Edit,
  ELIMINADO: Trash2,
  VISTO: Eye,
  APROBADO: CheckCircle,
  RECHAZADO: XCircle,
  SOLICITUD_APROBACION: Clock,
  DECISION_APROBADO: CheckCircle,
  DECISION_RECHAZADO: XCircle,
  ASIGNADO: User,
  COMENTARIO: FileText,
  DEFAULT: History,
};

const ACCION_COLORS = {
  CREADO: 'text-green-600 bg-green-50',
  ACTUALIZADO: 'text-blue-600 bg-blue-50',
  ELIMINADO: 'text-red-600 bg-red-50',
  APROBADO: 'text-green-600 bg-green-50',
  APROBADA: 'text-green-600 bg-green-50',
  RECHAZADO: 'text-red-600 bg-red-50',
  RECHAZADA: 'text-red-600 bg-red-50',
  SOLICITUD_APROBACION: 'text-amber-600 bg-amber-50',
  DEFAULT: 'text-slate-600 bg-slate-50',
};

const ENTIDAD_ICONS = {
  CASO: Briefcase,
  ALERTA: AlertTriangle,
  EMPRESA: Building2,
  APROBACION: CheckCircle,
  USUARIO: User,
  DEFAULT: FileText,
};

export default function HistorialActividad() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entidad_tipo: 'all',
    dias: '7',
    search: '',
  });
  const [stats, setStats] = useState(null);

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.entidad_tipo !== 'all') {
        params.append('entidad_tipo', filters.entidad_tipo);
      }
      params.append('dias', filters.dias);
      params.append('limit', '100');
      
      const response = await api.get(`/activity?${params.toString()}`);
      setActivities(response.data || []);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [filters.entidad_tipo, filters.dias]);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get(`/activity/stats?dias=${filters.dias}`);
      setStats(response.data);
    } catch (error) {
      // Stats solo disponibles para admin, ignorar error
    }
  }, [filters.dias]);

  useEffect(() => {
    loadActivities();
    loadStats();
  }, [loadActivities, loadStats]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    // Si es hoy, mostrar hora
    if (diff < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(diff / (60 * 60 * 1000));
      if (hours < 1) {
        const mins = Math.floor(diff / (60 * 1000));
        return `Hace ${mins} minutos`;
      }
      return `Hace ${hours} horas`;
    }
    
    // Si es ayer
    if (diff < 48 * 60 * 60 * 1000) {
      return `Ayer ${date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getIcon = (accion) => {
    const IconComponent = ACCION_ICONS[accion] || ACCION_ICONS.DEFAULT;
    return IconComponent;
  };

  const getColor = (accion) => {
    return ACCION_COLORS[accion] || ACCION_COLORS.DEFAULT;
  };

  const getEntidadIcon = (tipo) => {
    return ENTIDAD_ICONS[tipo] || ENTIDAD_ICONS.DEFAULT;
  };

  const filteredActivities = activities.filter(a => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      a.descripcion?.toLowerCase().includes(search) ||
      a.usuario_email?.toLowerCase().includes(search) ||
      a.entidad_id?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <History className="text-slate-600" />
            Historial de Actividad
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Registro de todas las acciones realizadas en el sistema
          </p>
        </div>
        
        <Button variant="outline" onClick={loadActivities}>
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-white">
            <div className="text-2xl font-bold text-slate-800">{stats.total_actividades}</div>
            <div className="text-sm text-slate-500">Total Actividades</div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="text-2xl font-bold text-slate-800">{stats.por_entidad?.CASO || 0}</div>
            <div className="text-sm text-slate-500">En Casos</div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="text-2xl font-bold text-slate-800">{stats.por_entidad?.ALERTA || 0}</div>
            <div className="text-sm text-slate-500">En Alertas</div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="text-2xl font-bold text-slate-800">{stats.usuarios_mas_activos?.length || 0}</div>
            <div className="text-sm text-slate-500">Usuarios Activos</div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 mb-6 bg-white">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-slate-600">
            <Filter size={16} />
            <span className="font-medium text-sm">Filtros:</span>
          </div>
          
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Buscar actividad..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
              />
            </div>
          </div>
          
          <Select
            value={filters.entidad_tipo}
            onValueChange={(val) => setFilters({ ...filters, entidad_tipo: val })}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTIDAD_TIPOS.map((tipo) => (
                <SelectItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select
            value={filters.dias}
            onValueChange={(val) => setFilters({ ...filters, dias: val })}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Últimas 24h</SelectItem>
              <SelectItem value="7">Última semana</SelectItem>
              <SelectItem value="30">Último mes</SelectItem>
              <SelectItem value="90">Últimos 3 meses</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Activity Timeline */}
      <Card className="bg-white">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">
            {filteredActivities.length} actividades encontradas
          </h2>
        </div>
        
        <div className="divide-y divide-slate-100">
          {filteredActivities.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No hay actividades registradas en el período seleccionado
            </div>
          ) : (
            filteredActivities.map((activity) => {
              const ActionIcon = getIcon(activity.accion);
              const EntidadIcon = getEntidadIcon(activity.entidad_tipo);
              const colorClass = getColor(activity.accion);
              
              return (
                <div
                  key={activity.id}
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`activity-${activity.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <ActionIcon size={18} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-800">
                          {activity.descripcion}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {activity.usuario_email}
                        </span>
                        
                        <span className="flex items-center gap-1">
                          <EntidadIcon size={14} />
                          {activity.entidad_tipo}
                        </span>
                        
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {formatDate(activity.fecha)}
                        </span>
                      </div>
                      
                      {/* Mostrar cambios si existen */}
                      {activity.datos_anteriores && activity.datos_nuevos && (
                        <div className="mt-2 p-2 bg-slate-50 rounded text-xs">
                          <div className="flex items-center gap-2 text-slate-600">
                            {Object.keys(activity.datos_nuevos).slice(0, 2).map((key) => (
                              <span key={key} className="flex items-center gap-1">
                                <span className="text-slate-400">{key}:</span>
                                <span className="line-through text-red-400">
                                  {String(activity.datos_anteriores[key] || '-').slice(0, 20)}
                                </span>
                                <ArrowRight size={10} />
                                <span className="text-green-600">
                                  {String(activity.datos_nuevos[key] || '-').slice(0, 20)}
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Badge de acción */}
                    <span className={`px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
                      {activity.accion}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>
    </div>
  );
}
